import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../client/components/ui/dialog";
import { Button } from "../../client/components/ui/button";
import { Input } from "../../client/components/ui/input";
import { Skeleton } from "../../client/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../client/components/ui/tooltip";
import { Badge } from "../../client/components/ui/badge";
import { Textarea } from "../../client/components/ui/textarea";
import {
  Check,
  Plus,
  FileInput,
  Pencil,
  Copy,
  Share2,
  Trash2,
  Lock,
} from "lucide-react";
import useTranslation from "../../client/hooks/useTranslation";
import { useLayoutsLibrary } from "./useLayoutsLibrary";
import LayoutPreviewGrid from "./LayoutPreviewGrid";
import LayoutShareSettingsDialog from "./LayoutShareSettingsDialog";
import ImportLayoutDialog, { type ImportDestination } from "./ImportLayoutDialog";
import RenameLayoutDialog from "./RenameLayoutDialog";
import DeleteLayoutDialog from "./DeleteLayoutDialog";
import {
  getEdgelyLayouts,
  getCommunityLayouts,
  getLayoutById,
  getMyLayouts,
  useQuery,
} from "wasp/client/operations";
import type { LayoutSummary, LayoutWithRowsAndSlots } from "../operations";
import { useToast } from "../../client/hooks/use-toast";

type Source = "my" | "edgely" | "community";

export interface LayoutLibraryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope?: "dashboard" | "pfm" | "custom-page" | "journal" | "analytics" | "sessions" | "discipline";
  isFreePlan: boolean;
  onUpgradeClick: () => void;
  onNewLayoutActivated?: () => void;
  onBeforeActivate?: () => Promise<void>;
  onActivate?: (layoutId: string) => Promise<void>;
  activeLayoutId?: string | null;
  pageLayout?: { id: string; name: string; shareToken?: string | null } | null;
  mode?: "default" | "new-page";
  onCreateBlankPage?: () => void;
}

export default function LayoutLibraryModal({
  open,
  onOpenChange,
  scope = "dashboard",
  isFreePlan,
  onUpgradeClick,
  onNewLayoutActivated,
  onBeforeActivate,
  onActivate,
  activeLayoutId: activeLayoutIdProp,
  pageLayout,
  mode = "default",
  onCreateBlankPage,
}: LayoutLibraryModalProps) {
  const t = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();

  const { layouts, activeLayout, create, duplicate, rename, remove, activate, setShareSettings, importWithDestination } =
    useLayoutsLibrary(scope);

  // For dashboard scope, also load custom-page layouts (and vice-versa) — they're widget-compatible
  const compatScope = scope === "dashboard" ? "custom-page" : scope === "custom-page" ? "dashboard" : null;
  const { data: compatLayouts } = useQuery(getMyLayouts, { scope: compatScope ?? "dashboard" }, { enabled: !!compatScope });

  const { data: edgelyLayouts, isLoading: edgelyLoading } = useQuery(getEdgelyLayouts, undefined);
  const { data: communityLayouts, isLoading: communityLoading } = useQuery(getCommunityLayouts, undefined);

  const [source, setSource] = useState<Source>("my");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Lazy load layout rows via useQuery — only fires when selectedId is set and source is "my"
  const { data: lazyLayoutData, isLoading: lazyLayoutLoading } = useQuery(
    getLayoutById,
    { layoutId: selectedId! },
    { enabled: source === "my" && !!selectedId }
  );

  const [showRename, setShowRename] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showNewLayout, setShowNewLayout] = useState(false);
  const [showImportLink, setShowImportLink] = useState(false);
  const [newLayoutName, setNewLayoutName] = useState("");
  const [importInput, setImportInput] = useState("");
  const [actionBusy, setActionBusy] = useState(false);

  const resolvedActiveLayoutId = activeLayoutIdProp !== undefined
    ? activeLayoutIdProp
    : (activeLayout?.id ?? null);

  const allMyLayouts = [
    ...(layouts as any[]),
    ...((compatLayouts as any[]) ?? []).filter((l: any) => !(layouts as any[]).find((p: any) => p.id === l.id)),
  ];
  const selectedMyLayout = source === "my"
    ? (allMyLayouts.find((l: any) => l.id === selectedId) ?? null)
    : null;
  const selectedEdgely = source === "edgely"
    ? ((edgelyLayouts as any[])?.find((l: any) => l.id === selectedId) ?? null)
    : null;
  const selectedCommunity = source === "community"
    ? ((communityLayouts as any[])?.find((l: any) => l.id === selectedId) ?? null)
    : null;

  const selectedLayout = selectedMyLayout ?? selectedEdgely ?? selectedCommunity;

  function handleSelectLayout(id: string) {
    setSelectedId(id);
  }

  function handleSourceChange(s: Source) {
    setSource(s);
    setSelectedId(null);
  }

  function getPreviewRows() {
    if (source === "edgely") return (selectedEdgely as any)?.rows ?? [];
    if (source === "community") return (selectedCommunity as any)?.rows ?? [];
    return (lazyLayoutData as any)?.rows ?? [];
  }

  const previewRows = getPreviewRows();
  const canCreate = !isFreePlan;
  const canDuplicate = !isFreePlan && !!selectedMyLayout;

  async function handleActivate() {
    if (actionBusy) return;
    if (!selectedId || !selectedMyLayout) return;
    setActionBusy(true);
    try {
      await onBeforeActivate?.();
      const isSameScope = (selectedMyLayout as any).scope === scope;
      if (isSameScope) {
        // Same scope: just switch the active layout
        if (onActivate) {
          await onActivate(selectedId);
        } else {
          await activate(selectedId);
        }
      } else {
        // Cross-scope (e.g. custom-page layout activated from dashboard):
        // Copy its content into the current active layout
        const currentLayoutId = resolvedActiveLayoutId ?? pageLayout?.id ?? undefined;
        if (currentLayoutId) {
          await importWithDestination(selectedId, "current", { currentLayoutId, useLayoutId: true });
        }
      }
      onOpenChange(false);
      onNewLayoutActivated?.();
    } finally {
      setActionBusy(false);
    }
  }

  async function handleImportConfirm(destination: ImportDestination) {
    if (!selectedLayout) return;
    setActionBusy(true);
    const currentLayoutId = destination === "current"
      ? (resolvedActiveLayoutId ?? pageLayout?.id ?? undefined)
      : undefined;
    // Edgely/community layouts: import by id directly (no shareToken needed)
    const useLayoutId = source === "edgely" || (source === "community" && !(selectedLayout as any).shareToken);
    const tokenOrId = useLayoutId ? (selectedLayout as any).id : (selectedLayout as any).shareToken;
    const result = await importWithDestination(tokenOrId, destination, { currentLayoutId, useLayoutId });
    setActionBusy(false);
    setShowImport(false);
    if (result) {
      onOpenChange(false);
      if (destination === "new-page" && (result as any).customPageId) {
        navigate(`/p/${(result as any).customPageId}`);
      } else {
        onNewLayoutActivated?.();
      }
    }
  }

  async function handleNewLayoutConfirm() {
    const trimmed = newLayoutName.trim();
    if (!trimmed) return;
    setActionBusy(true);
    try {
      const created = await create(trimmed);
      setShowNewLayout(false);
      setNewLayoutName("");
      if (created?.id) {
        const activated = await activate(created.id);
        if (activated) {
          onOpenChange(false);
          onNewLayoutActivated?.();
        }
      }
    } finally {
      setActionBusy(false);
    }
  }

  async function handleImportLinkConfirm() {
    const input = importInput.trim();
    if (!input) return;
    const match = input.match(/\/dashboard\/layouts\/share\/([A-Za-z0-9_-]{16})/);
    const token = match ? match[1] : input;
    if (!/^[A-Za-z0-9_-]{16}$/.test(token)) return;
    setActionBusy(true);
    try {
      const result = await importWithDestination(token, "save-only");
      setShowImportLink(false);
      setImportInput("");
      if (result?.id) {
        await activate(result.id);
        onOpenChange(false);
        onNewLayoutActivated?.();
      }
    } finally {
      setActionBusy(false);
    }
  }

  function getScopeLabel(s: string) {
    if (s === "dashboard") return t.dashboard.layouts.scopeDashboard;
    if (s === "pfm") return t.dashboard.layouts.scopePfm;
    if (s === "custom-page") return t.dashboard.layouts.scopeCustomPage;
    return s;
  }

  const isLoadingList = source === "edgely" ? edgelyLoading : source === "community" ? communityLoading : false;

  function renderList() {
    if (source === "my") {
      const pageLayoutItem = pageLayout && !allMyLayouts.find((l: any) => l.id === pageLayout.id)
        ? [{ id: pageLayout.id, name: pageLayout.name, isReadOnly: false, scope, isActive: resolvedActiveLayoutId === null, isPageLayout: true }]
        : [];
      const allLayouts = [...pageLayoutItem, ...allMyLayouts];

      if (allLayouts.length === 0) {
        return <p className="text-xs text-muted-foreground px-2 py-4">{t.dashboard.layouts.sourceMyLayouts}</p>;
      }
      return allLayouts.map((l: any) => {
        const wrongScope = l.scope === "pfm" ? scope !== "pfm" : scope === "pfm";
        const isActive = l.id === resolvedActiveLayoutId || (resolvedActiveLayoutId === null && !!l.isPageLayout);
        const isSelected = l.id === selectedId;
        const btn = (
          <button
            key={l.id}
            type="button"
            disabled={wrongScope}
            onClick={() => handleSelectLayout(l.id)}
            className={[
              "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2",
              isSelected ? "bg-primary/15 text-foreground" : "hover:bg-accent/60 text-foreground",
              wrongScope ? "opacity-40 cursor-not-allowed" : "",
            ].join(" ")}
          >
            <span className="flex-1 truncate">{l.name}</span>
            {l.isReadOnly && (
              <span className="text-[10px] text-muted-foreground bg-muted px-1 rounded">{t.dashboard.layouts.defaultBadge}</span>
            )}
            {isActive && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
          </button>
        );
        if (wrongScope) {
          return (
            <Tooltip key={l.id}>
              <TooltipTrigger asChild>{btn}</TooltipTrigger>
              <TooltipContent>{t.dashboard.layouts.wrongScopeTooltip.replace("{scope}", getScopeLabel(l.scope))}</TooltipContent>
            </Tooltip>
          );
        }
        return btn;
      });
    }

    if (isLoadingList) {
      return Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-10 mx-2 my-0.5 rounded-lg" />
      ));
    }

    if (source === "edgely") {
      const list = (edgelyLayouts as any[]) ?? [];
      if (list.length === 0) return <p className="text-xs text-muted-foreground px-2 py-4">{t.dashboard.layouts.edgelyEmpty}</p>;
      return list.map((l: any) => {
        const wrongScope = l.scope === "pfm" ? scope !== "pfm" : scope === "pfm";
        const isSelected = l.id === selectedId;
        const btn = (
          <button
            key={l.id}
            type="button"
            disabled={wrongScope}
            onClick={() => { setSelectedId(l.id); }}
            className={[
              "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
              isSelected ? "bg-primary/15 text-foreground" : "hover:bg-accent/60 text-foreground",
              wrongScope ? "opacity-40 cursor-not-allowed" : "",
            ].join(" ")}
          >
            <span className="flex items-center gap-2">
              <span className="flex-1 truncate">{l.shareDisplayName ?? l.name}</span>
              {l.requiredPlan && (
                <span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border border-yellow-500/30 shrink-0 ml-1">
                  {l.requiredPlan}
                </span>
              )}
              <Badge variant="outline" className="text-[10px] shrink-0">{t.dashboard.layouts.sourceEdgely}</Badge>
            </span>
          </button>
        );
        if (wrongScope) {
          return (
            <Tooltip key={l.id}>
              <TooltipTrigger asChild>{btn}</TooltipTrigger>
              <TooltipContent>{t.dashboard.layouts.wrongScopeTooltip.replace("{scope}", getScopeLabel(l.scope))}</TooltipContent>
            </Tooltip>
          );
        }
        return btn;
      });
    }

    if (source === "community") {
      const list = (communityLayouts as any[]) ?? [];
      if (list.length === 0) return <p className="text-xs text-muted-foreground px-2 py-4">{t.dashboard.layouts.communityEmpty}</p>;
      return list.map((l: any) => {
        const wrongScope = l.scope === "pfm" ? scope !== "pfm" : scope === "pfm";
        const isSelected = l.id === selectedId;
        const btn = (
          <button
            key={l.id}
            type="button"
            disabled={wrongScope}
            onClick={() => { setSelectedId(l.id); }}
            className={[
              "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
              isSelected ? "bg-primary/15 text-foreground" : "hover:bg-accent/60 text-foreground",
              wrongScope ? "opacity-40 cursor-not-allowed" : "",
            ].join(" ")}
          >
            <span className="block truncate">{l.shareDisplayName ?? l.name ?? t.dashboard.layouts.noName}</span>
            {l.authorUsername && (
              <span className="text-[10px] text-muted-foreground">
                {t.dashboard.layouts.authorLabel.replace("{username}", l.authorUsername)}
              </span>
            )}
          </button>
        );
        if (wrongScope) {
          return (
            <Tooltip key={l.id}>
              <TooltipTrigger asChild>{btn}</TooltipTrigger>
              <TooltipContent>{t.dashboard.layouts.wrongScopeTooltip.replace("{scope}", getScopeLabel(l.scope))}</TooltipContent>
            </Tooltip>
          );
        }
        return btn;
      });
    }
  }

  const showDescription =
    (source === "edgely" && !!(selectedEdgely as any)?.shareDescription) ||
    (source === "community" && !!(selectedCommunity as any)?.shareDescription) ||
    (source === "my" && !!(selectedMyLayout as any)?.isShared);

  return (
    <TooltipProvider delayDuration={200}>
      <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl h-[75vh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-6 pt-5 pb-0 shrink-0">
            <DialogTitle>
              {mode === "new-page" ? t.nav.addPage : t.dashboard.layouts.libraryTitle}
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-1 min-h-0 mt-4">
            {/* Col 1 — Source */}
            <div className="w-44 shrink-0 border-r border-border/50 flex flex-col gap-1 px-3 py-3">
              {(["my", "edgely", "community"] as Source[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => handleSourceChange(s)}
                  className={[
                    "w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                    source === s ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
                  ].join(" ")}
                >
                  {s === "my"
                    ? t.dashboard.layouts.sourceMyLayouts
                    : s === "edgely"
                    ? t.dashboard.layouts.sourceEdgely
                    : t.dashboard.layouts.sourceCommunity}
                </button>
              ))}

              {source === "my" && (
                <div className="mt-auto pt-4 space-y-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => { if (!canCreate) { onUpgradeClick(); return; } setNewLayoutName(""); setShowNewLayout(true); }}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-muted-foreground hover:bg-accent/50 hover:text-foreground transition-colors"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        {t.dashboard.layouts.newLayout}
                        {!canCreate && <Lock className="h-3 w-3 ml-auto" />}
                      </button>
                    </TooltipTrigger>
                    {!canCreate && <TooltipContent>{t.dashboard.layouts.upgradeForMultiple}</TooltipContent>}
                  </Tooltip>
                  <button
                    type="button"
                    onClick={() => { setImportInput(""); setShowImportLink(true); }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-muted-foreground hover:bg-accent/50 hover:text-foreground transition-colors"
                  >
                    <FileInput className="h-3.5 w-3.5" />
                    {t.dashboard.layouts.importLink}
                  </button>
                </div>
              )}
            </div>

            {/* Col 2 — Liste */}
            <div className="w-52 shrink-0 border-r border-border/50 overflow-y-auto flex flex-col gap-0.5 px-2 py-3">
              {renderList()}
            </div>

            {/* Col 3 — Preview + Actions */}
            <div className="flex-1 min-w-0 flex flex-col px-6 py-4 overflow-y-auto">
              {!selectedLayout ? (
                <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
                  {t.dashboard.layouts.previewEmpty}
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-base truncate">
                      {(selectedLayout as any).shareDisplayName ?? (selectedLayout as any).name}
                    </h3>
                    <Badge variant="outline" className="text-[10px] shrink-0">
                      {getScopeLabel((selectedLayout as any).scope)}
                    </Badge>
                  </div>
                  {isFreePlan && (selectedLayout as any)?.requiredPlan && (
                    <p className="text-xs text-yellow-500 mt-1 flex items-center gap-1 mb-3">
                      <Lock className="h-3 w-3" />
                      {t.dashboard.layouts.requiresPro}
                    </p>
                  )}

                  {source === "my" && lazyLayoutLoading ? (
                    <Skeleton className="h-[280px] rounded-lg" />
                  ) : (
                    <LayoutPreviewGrid rows={previewRows} />
                  )}

                  {showDescription && (
                    <div className="mt-4">
                      {source === "my" ? (
                        <Textarea
                          value={(selectedLayout as any).shareDescription ?? ""}
                          readOnly
                          rows={3}
                          className="text-xs text-muted-foreground resize-none"
                        />
                      ) : (
                        <p className="text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2">
                          {(selectedLayout as any).shareDescription}
                        </p>
                      )}
                    </div>
                  )}

                  {source === "my" && selectedMyLayout && !(selectedMyLayout as any).isReadOnly && (
                    <div className="flex gap-2 mt-4 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => setShowRename(true)}>
                        <Pencil className="h-3.5 w-3.5 mr-1" />{t.dashboard.layouts.actionsRename}
                      </Button>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!canDuplicate}
                            onClick={() => { if (!canDuplicate) { onUpgradeClick(); return; } duplicate(selectedId!); }}
                          >
                            <Copy className="h-3.5 w-3.5 mr-1" />{t.dashboard.layouts.actionsDuplicate}
                          </Button>
                        </TooltipTrigger>
                        {!canDuplicate && <TooltipContent>{t.dashboard.layouts.upgradeForMultiple}</TooltipContent>}
                      </Tooltip>
                      <Button size="sm" variant="outline" onClick={() => setShowShare(true)}>
                        <Share2 className="h-3.5 w-3.5 mr-1" />{t.dashboard.layouts.actionsShare}
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => setShowDelete(true)}>
                        <Trash2 className="h-3.5 w-3.5 mr-1" />{t.dashboard.layouts.actionsDelete}
                      </Button>
                    </div>
                  )}

                  <div className="mt-auto pt-4 flex gap-2 justify-end flex-wrap">
                    {mode === "default" && source === "my" && selectedMyLayout && (
                      <Button
                        disabled={actionBusy || (selectedMyLayout as any).id === resolvedActiveLayoutId}
                        onClick={handleActivate}
                      >
                        {(selectedMyLayout as any).scope !== scope
                          ? t.dashboard.layouts.importOnThisPage
                          : t.dashboard.layouts.importActivate}
                      </Button>
                    )}
                    {mode === "default" && source !== "my" && selectedLayout && (
                      <Button
                        onClick={() => {
                          if (isFreePlan && (selectedLayout as any)?.requiredPlan) {
                            onUpgradeClick();
                            return;
                          }
                          setShowImport(true);
                        }}
                        disabled={actionBusy}
                      >
                        {t.dashboard.layouts.importOnThisPage}
                      </Button>
                    )}
                    {mode === "new-page" && source !== "my" && selectedLayout && (
                      <Button
                        onClick={() => {
                          if (isFreePlan && (selectedLayout as any)?.requiredPlan) {
                            onUpgradeClick();
                            return;
                          }
                          handleImportConfirm("new-page");
                        }}
                        disabled={actionBusy}
                      >
                        {t.dashboard.layouts.newPageWithLayout}
                      </Button>
                    )}
                    {mode === "new-page" && onCreateBlankPage && (
                      <Button variant="outline" onClick={() => { onCreateBlankPage(); onOpenChange(false); }}>
                        {t.dashboard.layouts.newPageBlank}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {selectedMyLayout && (
        <>
          <RenameLayoutDialog
            open={showRename}
            onOpenChange={setShowRename}
            currentName={(selectedMyLayout as any).name}
            onConfirm={(name) => rename((selectedMyLayout as any).id, name)}
          />
          <DeleteLayoutDialog
            open={showDelete}
            onOpenChange={setShowDelete}
            layoutName={(selectedMyLayout as any).name}
            onConfirm={() => { remove((selectedMyLayout as any).id); setSelectedId(null); }}
          />
          <LayoutShareSettingsDialog
            open={showShare}
            onOpenChange={setShowShare}
            layout={selectedMyLayout as any}
            onSave={(opts) => setShareSettings((selectedMyLayout as any).id, opts)}
          />
        </>
      )}

      <ImportLayoutDialog
        open={showImport}
        onOpenChange={setShowImport}
        layoutName={(selectedLayout as any)?.shareDisplayName ?? (selectedLayout as any)?.name ?? ""}
        mode={mode === "new-page" ? "new-page" : "library"}
        canImportCurrent={
          // Can replace current page if a layout target exists.
          // Cross-scope allowed between dashboard and custom-page (widgets are compatible).
          // PFM scope is isolated — only importable on pfm pages.
          (!!resolvedActiveLayoutId || !!pageLayout) &&
          (!selectedLayout || (selectedLayout as any).scope !== "pfm" || scope === "pfm")
        }
        busy={actionBusy}
        onConfirm={handleImportConfirm}
      />

      <Dialog open={showNewLayout} onOpenChange={setShowNewLayout}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t.dashboard.layouts.newLayout}</DialogTitle>
          </DialogHeader>
          <Input
            value={newLayoutName}
            onChange={(e) => setNewLayoutName(e.target.value)}
            maxLength={50}
            autoFocus
            placeholder={t.dashboard.layouts.newLayoutPlaceholder}
            onKeyDown={(e) => { if (e.key === "Enter" && newLayoutName.trim().length >= 1) handleNewLayoutConfirm(); }}
          />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowNewLayout(false)}>{t.common.cancel}</Button>
            <Button disabled={newLayoutName.trim().length < 1 || actionBusy} onClick={handleNewLayoutConfirm}>{t.common.create}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showImportLink} onOpenChange={setShowImportLink}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t.dashboard.layouts.importPromptTitle}</DialogTitle>
          </DialogHeader>
          <Input
            value={importInput}
            onChange={(e) => setImportInput(e.target.value)}
            autoFocus
            placeholder={t.dashboard.layouts.importPromptInputLabel}
            onKeyDown={(e) => { if (e.key === "Enter") handleImportLinkConfirm(); }}
          />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowImportLink(false)}>{t.common.cancel}</Button>
            <Button disabled={actionBusy} onClick={handleImportLinkConfirm}>{t.dashboard.layouts.importLink}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
    </TooltipProvider>
  );
}
